/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useInboundRouteList.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    Custom hook for managing Inbound Routes list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchInboundRoutes } from '../api/inboundRouteApi';
import { InboundRoute } from '../types';

interface UseInboundRouteListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Inbound Routes list
 * Implements Domain Contextual Filtering pattern
 */
export function useInboundRouteList(options: UseInboundRouteListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchRoutes = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchInboundRoutes(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            domainId: effectiveDomainId
          },
          params.search,
          params.sortBy
        );

        return {
          data: result.data as InboundRoute[],
          pagination: result.pagination
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('inboundRoutes:messages.loadRoutesError');
        showError(message);
        return {
          data: [] as InboundRoute[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0
          }
        };
      }
    },
    [showError, t, effectiveDomainId]
  );

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('inboundRoutes:columns.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'numberPattern',
        label: t('inboundRoutes:columns.numberPattern'),
        visible: true,
        sortable: true
      },
      {
        key: 'destinationModule',
        label: t('inboundRoutes:columns.destinationModule'),
        visible: true,
        sortable: false
      },
      {
        key: 'priority',
        label: t('inboundRoutes:columns.priority'),
        visible: true,
        sortable: true
      },
      {
        key: 'enabled',
        label: t('inboundRoutes:columns.enabled'),
        visible: true,
        sortable: false
      },
      {
        key: 'createdAt',
        label: t('inboundRoutes:columns.createdAt'),
        visible: false,
        sortable: true
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ],
    [t]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchRoutes,
    initialColumns: availableColumns,
    initialSortBy: 'priority',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'inbound-routes',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as InboundRoute[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

